// DTOs for cross-context calls into sales, production and shipping [04 §6 F-SIZE-01, §13;
// 06 §3, §4, §5, §6, §10; 02 §10 G3/G4].
import { z } from 'zod';

import { businessDateSchema, idSchema } from '../kernel/index.ts';
import { currencyCodeSchema, qtyDecimalSchema, unitRateSchema } from '../units/index.ts';
import { gateCheckSchema } from './common.ts';
import { decimalStringSchema } from './scalars.ts';

// ---- sales: customer codes [04 §6 F-SIZE-01; 02 §10 G3] ----

export const resolveSizeQuerySchema = z.strictObject({
  customerId: idSchema,
  styleId: idSchema,
  token: z.string().min(1),
});

/** F-SIZE-01: mapped position, else grid label when the customer has no codes, else exception. */
export const resolvedSizeDtoSchema = z.discriminatedUnion('resolved', [
  z.strictObject({
    resolved: z.literal(true),
    sizePosition: z.int().positive(),
    sizeLabel: z.string().min(1),
  }),
  z.strictObject({ resolved: z.literal(false), reason: z.literal('unmapped_size') }),
]);

export const customerSkuCheckQuerySchema = z.strictObject({
  customerId: idSchema,
  skuIds: z.array(idSchema).min(1),
});

/** G3: customer SKU on every colourway/size before dispatch (override `shipping.override`). */
export const customerSkuCheckDtoSchema = z.strictObject({
  missing: z.array(z.strictObject({ skuId: idSchema, itemNo: z.string().length(24) })),
  gate: gateCheckSchema,
});

// ---- sales: PO parsers [06 §3.4; MB D10] ----

export const poProfileValues = ['qvc_uk', 'qvc_de', 'tsc', 'llm_fallback'] as const;

/** Where a value was read: page + box in PDF points; null for e-mail bodies. */
export const sourceBoxSchema = z.strictObject({
  page: z.int().positive(),
  x: z.number(),
  y: z.number(),
  width: z.number().nonnegative(),
  height: z.number().nonnegative(),
});

const parsedField = <T extends z.ZodType>(value: T) =>
  z.strictObject({
    value: value.nullable(),
    confidence: z.number().min(0).max(1),
    source: sourceBoxSchema.nullable(),
  });

export const parsePoInputSchema = z.strictObject({
  fileName: z.string().min(1),
  mediaType: z.string().min(1),
  pages: z.array(z.strictObject({ page: z.int().positive(), text: z.string() })),
  emailBody: z.string().nullable(),
  profileHint: z.enum(poProfileValues).nullable(),
});

/** ParsedPo: header + lines with per-field confidence; LLM fallback is never releasable [D10]. */
export const parsedPoDtoSchema = z.strictObject({
  profile: z.enum(poProfileValues),
  releasable: z.boolean(),
  customerPoNumber: parsedField(z.string().min(1)),
  poDate: parsedField(businessDateSchema),
  currency: parsedField(currencyCodeSchema),
  lines: z.array(
    z.strictObject({
      lineNo: z.int().positive(),
      customerItem: parsedField(z.string().min(1)),
      customerColour: parsedField(z.string().min(1)),
      customerSize: parsedField(z.string().min(1)),
      qty: parsedField(qtyDecimalSchema),
      unitPrice: parsedField(decimalStringSchema),
      deliveryDate: parsedField(businessDateSchema),
    }),
  ),
});

// ---- sales: programmes, orders [04 §13; 06 §3, §4] ----

export const approvedWorksheetQuerySchema = z.strictObject({
  customerId: idSchema,
  programmeId: idSchema.nullable(),
  styleIds: z.array(idSchema),
});

export const approvedWorksheetDtoSchema = z.strictObject({
  worksheetId: idSchema,
  version: z.int().positive(),
  lines: z.array(
    z.strictObject({
      styleId: idSchema,
      colourwayId: idSchema.nullable(),
      qty: qtyDecimalSchema.nullable(),
      unitPrice: unitRateSchema.nullable(),
      deliveryDate: businessDateSchema.nullable(),
    }),
  ),
});

/** colourway × size quantities; columns are the style's size-grid positions [MB D10]. */
export const sizeQtySchema = z.strictObject({
  sizePosition: z.int().positive(),
  qty: qtyDecimalSchema,
});

export const draftOrderFromImportInputSchema = z.strictObject({
  poImportId: idSchema,
  customerId: idSchema,
  customerPoNumber: z.string().min(1),
  currency: currencyCodeSchema,
  lines: z
    .array(
      z.strictObject({
        importLineNo: z.int().positive(),
        styleId: idSchema,
        colourwayId: idSchema,
        sizes: z.array(sizeQtySchema).min(1),
        unitPrice: unitRateSchema,
        deliveryDate: businessDateSchema.nullable(),
      }),
    )
    .min(1),
});

export const draftOrderRefDtoSchema = z.strictObject({
  salesOrderId: idSchema,
  number: z.string().min(1).nullable(),
  lines: z.array(z.strictObject({ importLineNo: z.int().positive(), orderLineId: idSchema })),
});

export const orderLineMatrixDtoSchema = z.strictObject({
  orderLineId: idSchema,
  salesOrderId: idSchema,
  styleId: idSchema,
  colourwayId: idSchema,
  sizes: z.array(
    z.strictObject({
      sizePosition: z.int().positive(),
      ordered: qtyDecimalSchema,
      shipped: qtyDecimalSchema,
    }),
  ),
});

// ---- production: cut pool [06 §5, §7 `GET /cutting-slips/{id}/pool`] ----

export const cutPoolDtoSchema = z.strictObject({
  cuttingSlipId: idSchema,
  colourways: z.array(
    z.strictObject({
      colourwayId: idSchema,
      sizes: z.array(
        z.strictObject({
          sizePosition: z.int().positive(),
          cut: qtyDecimalSchema,
          allocated: qtyDecimalSchema,
          available: qtyDecimalSchema,
        }),
      ),
    }),
  ),
});

// ---- shipping: labels G4, shipment data for documents [06 §6, §10, §11] ----

export const marketLabelQuerySchema = z.strictObject({
  customerId: idSchema,
  market: z.string().regex(/^[A-Z]{2}$/),
  skuIds: z.array(idSchema).min(1),
});

/** G4: market label content complete before dispatch (override `shipping.override`). */
export const marketLabelResultDtoSchema = z.strictObject({
  missing: z.array(z.strictObject({ skuId: idSchema, field: z.string().min(1) })),
  gate: gateCheckSchema,
});

export const shipmentDocumentDataDtoSchema = z.strictObject({
  shipmentId: idSchema,
  number: z.string().min(1),
  customerId: idSchema,
  shipDate: businessDateSchema.nullable(),
  currency: currencyCodeSchema,
  lines: z.array(
    z.strictObject({
      shipmentLineId: idSchema,
      skuId: idSchema,
      itemNo: z.string().length(24),
      customerSku: z.string().nullable(),
      qty: qtyDecimalSchema,
      unitPrice: unitRateSchema,
      hsCode: z.string().nullable(),
      originCountry: z
        .string()
        .regex(/^[A-Z]{2}$/)
        .nullable(),
      cartons: z.int().nonnegative(),
    }),
  ),
});

export type ResolveSizeQuery = z.output<typeof resolveSizeQuerySchema>;
export type ResolvedSizeDto = z.output<typeof resolvedSizeDtoSchema>;
export type CustomerSkuCheckQuery = z.output<typeof customerSkuCheckQuerySchema>;
export type CustomerSkuCheckDto = z.output<typeof customerSkuCheckDtoSchema>;
export type ParsePoInput = z.output<typeof parsePoInputSchema>;
export type ParsedPoDto = z.output<typeof parsedPoDtoSchema>;
export type ApprovedWorksheetQuery = z.output<typeof approvedWorksheetQuerySchema>;
export type ApprovedWorksheetDto = z.output<typeof approvedWorksheetDtoSchema>;
export type DraftOrderFromImportInput = z.output<typeof draftOrderFromImportInputSchema>;
export type DraftOrderRefDto = z.output<typeof draftOrderRefDtoSchema>;
export type OrderLineMatrixDto = z.output<typeof orderLineMatrixDtoSchema>;
export type CutPoolDto = z.output<typeof cutPoolDtoSchema>;
export type MarketLabelQuery = z.output<typeof marketLabelQuerySchema>;
export type MarketLabelResultDto = z.output<typeof marketLabelResultDtoSchema>;
export type ShipmentDocumentDataDto = z.output<typeof shipmentDocumentDataDtoSchema>;
